import type {
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  SlotLabelContentArg,
  SlotLabelMountArg,
} from '@fullcalendar/core';
import type { EventResizeStopArg } from '@fullcalendar/interaction';
import type { AuthenticatedUser } from '@src/features/users';
import { formatIsoDate, isSameDate, toTime } from '@src/shared/lib';
import { useCallback, useMemo, useRef } from 'react';
import {
  ABS_DAY_VIEW,
  canManageAbsenceUser,
  getAbsenceSelectionById,
  getAbsencesIdsForRange,
  getSlotDatefromSelection,
  getSlotFromEvent,
  getUserAbsences,
  isFullyOccupiedSlot,
  groupAbsencesByUserDate,
  hasMixedHourlyTypes,
  hydrateSelectedAbsence,
  isAbsenceSelected,
} from '../../lib';
import type {
  Absence,
  AbsenceType,
  ActivityTypeGroup,
  ResourceUser,
  SelectedAbsence,
  TimelineSlot,
} from '../../types';
import type { CalendarController } from './timeline';

const DB_CLICK_THRESHOLD = 300;

type TimelineInteractionState = {
  closeBalanceHistory: () => void;
  closeLegend: () => void;
  searchHighlightedUserId: number | null;
  selectedAbsence: SelectedAbsence | null;
  selectedSlot: TimelineSlot | null;
  selectedUserId: number | null;
  setSelectedAbsence: (selection: SelectedAbsence | null) => void;
  setSelectedSlot: (slot: TimelineSlot | null) => void;
  setSelectedUserId: (
    userId: number | null | ((prev: number | null) => number | null),
  ) => void;
  setSidebarMode: (mode: 'details' | 'schedule' | null) => void;
  setView: (arg: DatesSetArg['view']) => void;
  setVisibleRange: (range: { end: Date; start: Date }) => void;
  showBalanceHistory: boolean;
  showLegend: boolean;
  sidebarMode: 'details' | 'schedule' | null;
  suppressSelectionUntil: number;
  view: { type: string } | null;
};

type UseTimelineCalendarHandlersParams = {
  absences: Absence[];
  absenceTypes: AbsenceType[];
  activeSelection: TimelineSlot | null;
  activityTypeGroups: ActivityTypeGroup[];
  calendar: Pick<CalendarController, 'changeView' | 'setDayView' | 'unselect'>;
  currentUser: AuthenticatedUser | null;
  holidayLookup: {
    globalDates: Set<string>;
    isHolidayDate: (date: Date | string, branchId?: number | null) => boolean;
    tooltipsByDate: Map<string, string>;
  };
  hoveredDateKey: string | null;
  interactionState: TimelineInteractionState;
  isDayView: boolean;
  isMonthView: boolean;
  isSyncingSelectionRef: React.RefObject<boolean>;
  users: ResourceUser[];
};

type LastDates = null | { end: number; start: number };
type LastClick = { id: number | null; time: number };

function formatTooltipDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}.${month}.${year}`;
}

function getEventAbsenceId(event: EventApi): number {
  return Number(event.extendedProps.absenceId ?? event.id);
}

export default function useTimelineCalendarHandlers({
  absences,
  absenceTypes,
  activeSelection,
  activityTypeGroups,
  calendar,
  currentUser,
  holidayLookup,
  hoveredDateKey,
  interactionState,
  isDayView,
  isMonthView,
  isSyncingSelectionRef,
  users,
}: UseTimelineCalendarHandlersParams) {
  const lastDatesRef = useRef<LastDates>(null);
  const lastClickRef = useRef<LastClick>({ id: null, time: 0 });
  const userBranchIds = useMemo(
    () => new Map(users.map((user) => [user.id, user.branch?.id ?? null])),
    [users],
  );

  const isHolidayForUserDate = useCallback(
    (date: Date | string, userId?: number | null) => {
      const branchId = userId == null ? null : userBranchIds.get(userId);
      return holidayLookup.isHolidayDate(date, branchId);
    },
    [holidayLookup, userBranchIds],
  );

  const canInteractWithUser = useCallback(
    (targetUserId: number | null | undefined) =>
      canManageAbsenceUser(targetUserId, users, currentUser),
    [currentUser, users],
  );

  const isSameAbsenceSelection = useCallback(
    (currentIds: number[], nextIds: number[]) => {
      const currentId = currentIds[0];
      const nextId = nextIds[0];

      if (!currentId || !nextId) {
        return false;
      }

      const currentSelection = getAbsenceSelectionById(currentId, absences);
      const nextSelection = getAbsenceSelectionById(nextId, absences);

      if (!currentSelection || !nextSelection) {
        return false;
      }

      if (currentSelection.userId !== nextSelection.userId) {
        return false;
      }

      if (
        currentSelection.absenceIds.length !== nextSelection.absenceIds.length
      ) {
        return false;
      }

      return currentSelection.absenceIds.every(
        (absenceId, index) => absenceId === nextSelection.absenceIds[index],
      );
    },
    [absences],
  );

  const shouldIgnoreSelection = useCallback(
    () =>
      isSyncingSelectionRef.current ||
      Date.now() < interactionState.suppressSelectionUntil,
    [interactionState.suppressSelectionUntil, isSyncingSelectionRef],
  );

  const onSelect = useCallback(
    (selectionInfo: DateSelectArg) => {
      if (shouldIgnoreSelection()) {
        return;
      }

      const slotData = getSlotDatefromSelection(selectionInfo, absences);
      if (!canInteractWithUser(slotData.userId)) {
        calendar.unselect();
        return;
      }
      if (isDayView && slotData.selectedIds.length > 0) {
        calendar.unselect();
        return;
      }
      const showAbsenceDetails =
        isAbsenceSelected(slotData, absences) ||
        (!isDayView && isFullyOccupiedSlot(slotData, absences));

      if (showAbsenceDetails) {
        const primaryAbsence = absences.find(
          (a) => a.id === slotData.selectedIds[0],
        );
        const selection = {
          userId: slotData.userId,
          start: slotData.start,
          end: slotData.end,
          startTime: primaryAbsence?.startTime ?? slotData.startTime,
          endTime: primaryAbsence?.endTime ?? slotData.endTime,
          absenceIds: slotData.selectedIds,
          primaryAbsenceId: slotData.selectedIds[0],
        };
        const keepEditMode =
          isAbsenceSelected(slotData, absences) &&
          !!interactionState.selectedSlot &&
          interactionState.sidebarMode === 'schedule' &&
          isSameAbsenceSelection(
            interactionState.selectedSlot.selectedIds,
            slotData.selectedIds,
          );

        interactionState.setSelectedAbsence(
          hydrateSelectedAbsence(selection, absences),
        );
        interactionState.setSelectedSlot(keepEditMode ? slotData : null);
        interactionState.setSidebarMode(keepEditMode ? 'schedule' : 'details');
        if (slotData.userId)
          interactionState.setSelectedUserId(slotData.userId);
        return;
      }
      interactionState.setSelectedAbsence(null);
      interactionState.setSidebarMode('schedule');
      interactionState.setSelectedSlot(slotData);
      if (slotData.userId) interactionState.setSelectedUserId(slotData.userId);
    },
    [
      absences,
      calendar,
      canInteractWithUser,
      interactionState,
      isDayView,
      isSameAbsenceSelection,
      shouldIgnoreSelection,
    ],
  );

  const onAbsenceClick = useCallback(
    (info: EventClickArg) => {
      const now = Date.now();
      const id = getEventAbsenceId(info.event);
      const { id: prevId, time: prevTime } = lastClickRef.current;
      const isSame = prevId === id;
      const isDbl = isSame && now - prevTime < DB_CLICK_THRESHOLD;

      const absenceId = getEventAbsenceId(info.event);
      const absence = absences.find((item) => item.id === absenceId);
      if (!canInteractWithUser(absence?.userId)) {
        return;
      }

      const isMonthViewPartialAbsence =
        !!absence && isMonthView && !absence.allDay;

      const isMonthViewMultiAbsence =
        !!absence &&
        isMonthView &&
        absences.filter(
          (item) =>
            item.userId === absence.userId && item.dateStr === absence.dateStr,
        ).length > 1;

      if (isMonthViewMultiAbsence) {
        lastClickRef.current = { id, time: now };

        if (interactionState.showLegend) {
          interactionState.closeLegend();
        }
        if (interactionState.showBalanceHistory) {
          interactionState.closeBalanceHistory();
        }

        if (isDbl) {
          const sameDayAbsences = absences
            .filter(
              (item) =>
                item.userId === absence.userId &&
                item.dateStr === absence.dateStr,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            );

          const firstAbsence = sameDayAbsences[0];
          if (firstAbsence) {
            const selection = getAbsenceSelectionById(
              firstAbsence.id,
              absences,
              true,
            );
            if (selection) {
              interactionState.setSelectedAbsence(selection);
              interactionState.setSidebarMode('details');
            }
          }
          interactionState.setSelectedSlot(null);
          calendar.setDayView(absence.date);
          return;
        }

        const sameDayIds = absences
          .filter(
            (item) =>
              item.userId === absence.userId &&
              item.dateStr === absence.dateStr,
          )
          .map((item) => item.id);

        interactionState.setSidebarMode('details');
        interactionState.setSelectedAbsence(
          hydrateSelectedAbsence(
            {
              userId: absence.userId,
              start: absence.dateStr,
              end: absence.dateStr,
              startTime: null,
              endTime: null,
              absenceIds: sameDayIds,
              primaryAbsenceId: sameDayIds[0],
              isMultiEvent: true,
            },
            absences,
          ),
        );
        interactionState.setSelectedSlot(null);
        interactionState.setSelectedUserId(absence.userId);
        return;
      }

      if (isMonthViewPartialAbsence && isDbl) {
        calendar.setDayView(absence.date);
        lastClickRef.current = { id, time: now };
        return;
      }

      let selection = getAbsenceSelectionById(absenceId, absences, true);
      if (isDbl) {
        selection = getAbsenceSelectionById(absenceId, absences, isDayView);
      }
      if (selection) {
        interactionState.setSidebarMode('details');
        interactionState.setSelectedAbsence(selection);
        interactionState.setSelectedSlot(null);
        if (absence?.userId) interactionState.setSelectedUserId(absence.userId);
      }

      lastClickRef.current = { id, time: now };
    },
    [
      absences,
      calendar,
      canInteractWithUser,
      interactionState,
      isDayView,
      isMonthView,
    ],
  );

  const eventClassNames = useCallback(
    (arg: { event: EventApi }) => {
      const event = arg.event;
      if (event.display === 'background') {
        return event.classNames;
      }

      const classes = ['abs-base'];
      const day = event.start?.getDay();
      if (day === 0 || day === 6) {
        classes.push('abs-weekend');
      }
      const eventUserId = event.extendedProps.userId as number | undefined;
      if (event.start && isHolidayForUserDate(event.start, eventUserId)) {
        classes.push('abs-holiday');
      }
      const pos = event.extendedProps.block_position;
      if (pos && isMonthView) {
        classes.push(`abs-${pos}`);
      }
      if (!event.allDay && isMonthView) {
        classes.push('abs-partial');
      }
      if (isDayView) {
        classes.push('abs-day-event');
      }
      if (event.extendedProps.neutralColour) {
        classes.push('abs-neutral-colour');
      }
      const eventAbsenceId = getEventAbsenceId(event);
      if (
        activeSelection?.selectedIds.includes(eventAbsenceId) ||
        activeSelection?.editedAbsenceIds?.includes(eventAbsenceId)
      ) {
        classes.push('abs-selected');
      }
      if (
        !canInteractWithUser(event.extendedProps.userId as number | undefined)
      ) {
        classes.push('abs-readonly');
      }
      const absenceType = absenceTypes.find(
        (t) => t.id === event.extendedProps.type,
      );
      const approvable =
        absenceType?.approvableManager || absenceType?.approvableHr;
      if (approvable) {
        const hrApprovalStatus = event.extendedProps.approvedHr;
        const managerApprovalStatus = event.extendedProps.approvedManager;

        if (hrApprovalStatus === null && managerApprovalStatus === null) {
          classes.push('abs-pending-approval');
        }
        if (hrApprovalStatus === false || managerApprovalStatus === false) {
          classes.push('abs-rejected-approval');
        }
      }
      if (event.extendedProps.tentative) {
        classes.push('abs-tentative');
      }
      return classes;
    },
    [
      absenceTypes,
      activeSelection,
      canInteractWithUser,
      isDayView,
      isHolidayForUserDate,
      isMonthView,
    ],
  );

  const selectAllow = useCallback(
    (selectionInfo: {
      allDay: boolean;
      end: Date;
      resource?: { extendedProps?: { userId?: number } };
      start: Date;
    }) => {
      const selectedUserId = selectionInfo.resource?.extendedProps?.userId;
      if (!canInteractWithUser(selectedUserId)) {
        return false;
      }

      if (!currentUser?.isAdmin) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectionInfo.start < today) {
          const selectedUserId = selectionInfo.resource?.extendedProps?.userId;
          if (!selectedUserId || selectedUserId === currentUser?.id) {
            return false;
          }
          const targetUser = users.find((u) => u.id === selectedUserId);
          const isHr = targetUser?.hrId === currentUser?.id;
          const isManager = targetUser?.manager?.id === currentUser?.id;
          if (!isHr && !isManager) {
            return false;
          }
        }
      }

      if (!isDayView || !selectedUserId) {
        return true;
      }

      const overlappingIds = getAbsencesIdsForRange(
        selectionInfo.start,
        selectionInfo.end,
        getUserAbsences(selectedUserId, absences),
        {
          dayView: !selectionInfo.allDay,
          startTime: selectionInfo.allDay ? null : toTime(selectionInfo.start),
          endTime: selectionInfo.allDay ? null : toTime(selectionInfo.end),
        },
      );

      return overlappingIds.length === 0;
    },
    [absences, canInteractWithUser, currentUser, isDayView, users],
  );

  const eventAllow = useCallback(
    (dropInfo: DateSpanApi, draggedEvent: EventApi | null) => {
      const selectedUserId =
        dropInfo.resource?.extendedProps?.userId ??
        (draggedEvent?.extendedProps.userId as number | undefined);
      if (!canInteractWithUser(selectedUserId)) {
        return false;
      }

      if (!isDayView || !selectedUserId) {
        return true;
      }

      const overlappingIds = getAbsencesIdsForRange(
        dropInfo.start,
        dropInfo.end,
        getUserAbsences(selectedUserId, absences),
        {
          dayView: !dropInfo.allDay,
          startTime: dropInfo.allDay ? null : toTime(dropInfo.start),
          endTime: dropInfo.allDay ? null : toTime(dropInfo.end),
          excludeIds: draggedEvent
            ? [getEventAbsenceId(draggedEvent)]
            : undefined,
        },
      );

      return overlappingIds.length === 0;
    },
    [absences, canInteractWithUser, isDayView],
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      const newStart = arg.start.getTime();
      const newEnd = arg.end.getTime();

      const { start: lastStart, end: lastEnd } = lastDatesRef.current || {};
      if (newStart !== lastStart || newEnd !== lastEnd) {
        lastDatesRef.current = { start: newStart, end: newEnd };
        interactionState.setVisibleRange({ start: arg.start, end: arg.end });
      }
      if (interactionState.view?.type !== arg.view.type) {
        interactionState.setView(arg.view);
      }
    },
    [interactionState],
  );

  const slotLabelClassNames = useCallback(
    (arg: SlotLabelContentArg) => {
      const classes: string[] = [];
      const day = arg.date?.getDay?.();
      const isWeekend = day === 0 || day === 6;
      const isToday = arg.date ? isSameDate(arg.date, new Date()) : false;
      const isHovered = arg.date
        ? formatIsoDate(arg.date) === hoveredDateKey
        : false;
      const isMonthViewWeekdayRow = !isDayView && arg.level === 1;
      const isMonthViewDayRow = !isDayView && arg.level === 2;
      const shouldApplyMonthViewHeaderStyling = !isDayView && arg.level > 0;

      if (arg.level === 0) {
        classes.push('fc-month-slot-label');
      }
      if (isMonthViewWeekdayRow) {
        classes.push('fc-weekday-slot-label');
      }
      if (isMonthViewDayRow) {
        classes.push('fc-daynum-slot-label');
      }
      if (isWeekend && shouldApplyMonthViewHeaderStyling) {
        classes.push('fc-weekend-slot-label');
      }
      const dateKey = arg.date ? formatIsoDate(arg.date) : '';
      const isHoliday =
        arg.date &&
        (holidayLookup.globalDates.has(dateKey) ||
          holidayLookup.tooltipsByDate.has(dateKey));
      if (isHoliday && shouldApplyMonthViewHeaderStyling) {
        classes.push('fc-holiday-slot-label');
      }
      if (isToday && shouldApplyMonthViewHeaderStyling) {
        classes.push('fc-today-slot-label');
      }
      if (isHovered && shouldApplyMonthViewHeaderStyling) {
        classes.push('fc-hovered-slot-label');
      }

      return classes;
    },
    [
      holidayLookup.globalDates,
      holidayLookup.tooltipsByDate,
      hoveredDateKey,
      isDayView,
    ],
  );

  const onSlotLabelClick = useCallback(
    (date: Date) => {
      calendar.changeView(ABS_DAY_VIEW, date);
    },
    [calendar],
  );

  const slotLabelDidMount = useCallback(
    (mountArg: SlotLabelMountArg) => {
      if (mountArg.view.type === ABS_DAY_VIEW || !mountArg.date) {
        return;
      }
      const isClickableMonthLabel =
        mountArg.level === 1 || mountArg.level === 2;
      if (!isClickableMonthLabel) {
        return;
      }
      mountArg.el.style.cursor = 'pointer';
      mountArg.el.onclick = () => onSlotLabelClick(mountArg.date as Date);

      const dateKey = formatIsoDate(mountArg.date);
      if (holidayLookup.tooltipsByDate.has(dateKey)) {
        mountArg.el.setAttribute('data-holiday-date', dateKey);
      }
    },
    [onSlotLabelClick, holidayLookup.tooltipsByDate],
  );

  const tooltipMap = useMemo(() => {
    const map = new Map<number, string>();
    const grouped = groupAbsencesByUserDate(absences);

    for (const sameDayUserAbsences of grouped.values()) {
      if (!isDayView && hasMixedHourlyTypes(sameDayUserAbsences)) {
        const text = `Multiple events: ${formatTooltipDate(sameDayUserAbsences[0].dateStr)}`;
        for (const item of sameDayUserAbsences) {
          map.set(item.id, text);
        }
        continue;
      }

      for (const absence of sameDayUserAbsences) {
        if (map.has(absence.id)) continue;

        const selection = getAbsenceSelectionById(absence.id, absences);
        if (!selection) continue;

        const absenceType = absenceTypes.find((t) => t.id === absence.typeId);
        const group = absenceType
          ? activityTypeGroups.find((g) => g.id === absenceType.groupId)
          : undefined;
        const groupName = group?.name ?? absenceType?.name ?? '';
        if (!groupName) continue;

        const start = formatTooltipDate(selection.start);
        const end = formatTooltipDate(selection.end);
        const text =
          start === end
            ? `${groupName}: ${start}`
            : `${groupName}: ${start} - ${end}`;

        for (const id of selection.absenceIds) {
          map.set(id, text);
        }
      }
    }
    return map;
  }, [absences, absenceTypes, activityTypeGroups, isDayView]);

  const eventContent = useCallback(
    (args: EventContentArg) => {
      const { approvedHr, approvedManager } = args.event.extendedProps;
      const isRejected = approvedHr === false || approvedManager === false;

      const label = args.event.extendedProps.label;
      if (label) {
        if (isRejected) {
          return {
            html: '<div class="abs-event-content abs-rejected-x">X</div>',
          };
        }
        return {
          html: `<div class="abs-event-content">${label}</div>`,
        };
      }
      const showWorkdayCount = args.event.extendedProps.show_workday_count;
      if (showWorkdayCount) {
        if (isDayView) {
          return '';
        }
        if (!args.event.allDay) {
          return { html: '<div class="abs-workday-count">...</div>' };
        }

        if (isRejected) {
          return {
            html: '<div class="abs-workday-count abs-rejected-x">X</div>',
          };
        }

        const count = args.event.extendedProps.workday_count;
        return { html: `<div class="abs-workday-count">${count}</div>` };
      }
      return '';
    },
    [isDayView],
  );

  const eventDidMount = useCallback(
    (arg: { el: HTMLElement; event: EventApi }) => {
      if (arg.event.display === 'background') return;

      const colourFg = arg.event.extendedProps.colourFg as string | undefined;
      const colourBg = arg.event.extendedProps.colourBg as string | undefined;
      const neutralColour = arg.event.extendedProps.neutralColour as
        | boolean
        | undefined;

      arg.el.style.removeProperty('--abs-colour-bg');
      arg.el.style.removeProperty('--abs-colour-fg');

      if (neutralColour) {
        const absenceId = getEventAbsenceId(arg.event);
        if (tooltipMap.has(absenceId)) {
          arg.el.setAttribute('data-tooltip-id', String(absenceId));
        }
        return;
      }

      if (colourBg) {
        arg.el.style.setProperty('--abs-colour-bg', colourBg);
      }
      if (colourFg) {
        arg.el.style.setProperty('--abs-colour-fg', colourFg);
      }

      const absenceId = getEventAbsenceId(arg.event);
      if (tooltipMap.has(absenceId)) {
        arg.el.setAttribute('data-tooltip-id', String(absenceId));
      }
    },
    [tooltipMap],
  );

  const onEventResize = useCallback(
    (info: EventResizeStopArg) => {
      interactionState.setSelectedAbsence(null);
      interactionState.setSidebarMode('schedule');
      interactionState.setSelectedSlot(getSlotFromEvent(info.event));
    },
    [interactionState],
  );

  const resourceRowClassNames = useCallback(
    (arg: { resource: { extendedProps?: { userId?: number } } }) => {
      const userId = arg.resource.extendedProps?.userId;
      const activeHighlightId =
        interactionState.searchHighlightedUserId ??
        interactionState.selectedUserId;
      return userId != null && userId === activeHighlightId
        ? ['absence-resource-selected']
        : [];
    },
    [interactionState.selectedUserId, interactionState.searchHighlightedUserId],
  );

  return {
    canInteractWithUser,
    eventAllow,
    eventClassNames,
    eventContent,
    eventDidMount,
    eventTooltipMap: tooltipMap,
    handleDatesSet,
    holidayTooltipsByDate: holidayLookup.tooltipsByDate,
    onAbsenceClick,
    onEventResize,
    onSelect,
    resourceRowClassNames,
    selectAllow,
    slotLabelClassNames,
    slotLabelDidMount,
  };
}
